import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { AxiosInstance } from 'axios';
import { z } from 'zod/v4';

import {
  type ApiError,
  addActionNameToMutationResult,
  handleZodError,
  useAxiosInstance,
} from './utils';

const CreateSummaryFeedbackSchemaResponse = z.object({});

export type CreateSummaryFeedbackResponse = z.infer<typeof CreateSummaryFeedbackSchemaResponse>;

const CreateSummaryFeedbackSchema = z.object({
  debtorId: z.coerce.string(),
  summaryId: z.string(),
  rating: z.boolean(),
});

export type CreateSummaryFeedbackVariables = z.infer<typeof CreateSummaryFeedbackSchema>;

export async function createSummaryFeedbackQueryFn(
  axiosInstance: Promise<AxiosInstance>,
  variables: CreateSummaryFeedbackVariables,
) {
  const instance = await axiosInstance;
  const { data } = await instance.post(
    `ai_context_provider/summaries/${variables.summaryId}/feedbacks`,
    {
      rating: variables.rating,
    },
  );

  const parseResult = CreateSummaryFeedbackSchemaResponse.safeParse(data);
  if (!parseResult.success) {
    handleZodError(parseResult, 'debtorAiSummary');
    throw parseResult.error;
  }

  return parseResult.data;
}

export function useCreateSummaryFeedback() {
  const client = useQueryClient();
  const axiosInstance = useAxiosInstance();

  const mutation = useMutation<
    CreateSummaryFeedbackResponse,
    ApiError,
    CreateSummaryFeedbackVariables
  >({
    mutationKey: ['createSummaryFeedback'],
    mutationFn: data => createSummaryFeedbackQueryFn(axiosInstance, data),
    onSuccess: (_data, _variables) => {
      client.invalidateQueries({
        queryKey: ['aiSummary'],
      });
    },
  });

  return addActionNameToMutationResult<
    CreateSummaryFeedbackResponse,
    ApiError,
    CreateSummaryFeedbackVariables,
    'createSummaryFeedback'
  >('createSummaryFeedback', mutation);
}
